import { DocumentNode } from 'graphql';
import gql from 'graphql-tag';
// @ts-ignore
import { dataIdFromObject } from 'lib/apollo/cache/memory';
import { getApolloClientSingleton } from 'lib/apollo/client/browser';
import RemindAPI from 'lib/RemindAPI';
import { reloadIfUnauthenticated } from 'coaching/lib/authError';
import CoachingConstants from 'coaching/CoachingConstants';
import errorLogger from 'utils/errorLogger';
import ACCEPT_COACHING_INVITATION from 'coaching/mutations/AcceptCoachingInvitation.gql';
import ADD_STUDENT_MUTATION from 'coaching/mutations/AddStudentToCoachingByDevice.gql';
import ADMIN_DELETE_COACHING_SESSION_INSTANCE from 'coaching/mutations/AdminDeleteCoachingSessionInstance.gql';
import ADMIN_DELETE_COACHING_SESSION_SCHEDULE from 'coaching/mutations/AdminDeleteCoachingSessionSchedule.gql';
import CREATE_COACH_MUTATION from 'coaching/mutations/CreateCoach.gql';
import UPDATE_COACH_MUTATION from 'coaching/mutations/UpdateCoach.gql';
import CREATE_COACHING_RESERVATION_MUTATION from 'coaching/mutations/CreateCoachingReservation.gql';
import CREATE_COACHING_SESSION_MUTATION from 'coaching/mutations/CreateCoachingSession.gql';
import DELETE_COACHING_SESSION_MUTATION from 'coaching/mutations/DeleteCoachingSession.gql';
import DELETE_STUDENT_INVITATION_MUTATION from 'coaching/mutations/DeleteStudentInvitation.gql';
import GET_COACH_AVAILABILITY_FOR_EVENT_QUERY from 'coaching/queries/GetCoachAvailabilityForEvent.gql';
import GET_CREATE_COACH_FIELDS from 'coaching/queries/GetCreateCoachFields.gql';
import GET_COACH_STATUS_VALUES from 'coaching/queries/GetCoachStatusValues.gql';
import GET_STUDENTS_FAMILY_MEMBERS_DATA_QUERY from 'coaching/queries/GetStudentsFamilyMembers.gql';
import GET_PROSPECTIVE_OPTIONS_DATA_QUERY from 'coaching/queries/GetProspectiveOptions.gql';
import COACHES_QUERY from 'coaching/queries/Coaches.gql';
import GET_NAME_FOR_USER_UUID_QUERY from 'coaching/queries/GetNameForUserUuid.gql';
import GET_COACHING_USER_STRIPE_SOURCE from 'coaching/queries/GetUserStripeSource.gql';
import PATCH_PARTICIPANT_MUTATION from 'coaching/mutations/PatchParticipant.gql';
import PURCHASE_COACHING_WITH_CONNECTED_STUDENT_MUTATION from 'coaching/mutations/PurchaseCoachingProductWithConnectedStudent.gql';
import PURCHASE_COACHING_WITH_NO_CONNECTED_STUDENT_MUTATION from 'coaching/mutations/PurchaseCoachingProductWithNoConnectedStudent.gql';
import REFUND_CHARGE from 'coaching/mutations/RefundCharge.gql';
import RESCHEDULE_COACHING_SESSION_INSTANCE_MUTATION from 'coaching/mutations/RescheduleCoachingSessionInstance.gql';
import SAVE_AVAILABILITY_SCHEDULE_MUTATION from 'coaching/mutations/SaveAvailabilitySchedule.gql';
import SAVE_COACH_PAUSE_MUTATION from 'coaching/mutations/SaveCoachPause.gql';
import CANCEL_COACH_PAUSE_MUTATION from 'coaching/mutations/CancelCoachPause.gql';
import SAVE_SINGLE_DAY_AVAILABILITY_OVERRIDE_MUTATION from 'coaching/mutations/SaveSingleDayAvailabilityOverride.gql';
import SET_COACHING_USER_STRIPE_SOURCE from 'coaching/mutations/SetCoachingUserStripeSource.gql';
import MAYBE_CREATE_USER_AND_DEVICE from 'coaching/mutations/MaybeCreateUserAndDevice.gql';
import CREATE_TUTORING_RESERVATION_MUTATION from 'coaching/mutations/CreateTutoringReservation.gql';
import CONFIRM_TUTORING_RESERVATION_MUTATION from 'coaching/mutations/ConfirmTutoringReservation.gql';
import ADD_STUDENT_TO_TUTORING_MUTATION from 'coaching/queries/AddStudentToTutoringByDevice.gql';
import AVAILABLE_COACHING_SESSIONS_QUERY from 'coaching/queries/AvailableCoachingSessions.gql';

import {
  CoachingEventDefinitionInput,
  CoachingParticipantInput,
  CoachingSessionInput,
  AvailabilityScheduleInput,
  SingleDayAvailabilityOverrideInput,
  PurchaserRole,
  SaveCoachPauseInput,
} from 'globalTypes';
import {
  AddStudentToCoachingByDevice,
  AddStudentToCoachingByDeviceVariables,
} from 'coaching/mutations/__generated__/AddStudentToCoachingByDevice';
import {
  AdminDeleteCoachingSessionInstance,
  AdminDeleteCoachingSessionInstanceVariables,
} from 'coaching/mutations/__generated__/AdminDeleteCoachingSessionInstance';
import {
  AdminDeleteCoachingSessionSchedule,
  AdminDeleteCoachingSessionScheduleVariables,
} from 'coaching/mutations/__generated__/AdminDeleteCoachingSessionSchedule';
import { CreateCoach, CreateCoachVariables } from 'coaching/mutations/__generated__/CreateCoach';
import {
  CreateCoachingReservation,
  CreateCoachingReservationVariables,
} from 'coaching/mutations/__generated__/CreateCoachingReservation';
import {
  CreateCoachingSession,
  CreateCoachingSessionVariables,
} from 'coaching/mutations/__generated__/CreateCoachingSession';
import {
  DeleteCoachingSession,
  DeleteCoachingSessionVariables,
} from 'coaching/mutations/__generated__/DeleteCoachingSession';
import {
  DeleteStudentInvitation,
  DeleteStudentInvitationVariables,
} from 'coaching/mutations/__generated__/DeleteStudentInvitation';
import {
  CreateTutoringReservation,
  CreateTutoringReservationVariables,
} from 'coaching/mutations/__generated__/CreateTutoringReservation';
import {
  ConfirmTutoringReservation,
  ConfirmTutoringReservationVariables,
  ConfirmTutoringReservation_coaching_confirmTutoringReservation_error,
} from 'coaching/mutations/__generated__/ConfirmTutoringReservation';
import {
  GetCoachAvailabilityForEvent,
  GetCoachAvailabilityForEventVariables,
} from 'coaching/queries/__generated__/GetCoachAvailabilityForEvent';
import {
  GetNameForUserUuid,
  GetNameForUserUuidVariables,
} from 'coaching/queries/__generated__/GetNameForUserUuid';
import { GetCreateCoachFields } from 'coaching/queries/__generated__/GetCreateCoachFields';
import { GetCoachStatusValues } from 'coaching/queries/__generated__/GetCoachStatusValues';
import {
  GetStudentsFamilyMembers,
  GetStudentsFamilyMembers_coaching_studentFamilyMembers,
} from 'coaching/queries/__generated__/GetStudentsFamilyMembers';
import {
  AvailableCoachingSessions,
  AvailableCoachingSessionsVariables,
  AvailableCoachingSessions_coaching_availableCoachingSessions_availableSessions,
  AvailableCoachingSessions_coaching_availableCoachingSessions_timeOff,
} from 'coaching/queries/__generated__/AvailableCoachingSessions';
import { GetProspectiveOptions } from 'coaching/queries/__generated__/GetProspectiveOptions';
import {
  PatchParticipant,
  PatchParticipantVariables,
} from 'coaching/mutations/__generated__/PatchParticipant';
import {
  PurchaseCoachingProductWithConnectedStudent,
  PurchaseCoachingProductWithConnectedStudentVariables,
  PurchaseCoachingProductWithConnectedStudent_coaching_purchaseCoachingSessionWithConnectedStudent,
} from 'coaching/mutations/__generated__/PurchaseCoachingProductWithConnectedStudent';
import {
  PurchaseCoachingProductWithNoConnectedStudent,
  PurchaseCoachingProductWithNoConnectedStudentVariables,
  PurchaseCoachingProductWithNoConnectedStudent_coaching_purchaseCoachingSessionWithNoConnectedStudent,
} from 'coaching/mutations/__generated__/PurchaseCoachingProductWithNoConnectedStudent';
import {
  RescheduleCoachingSessionInstance,
  RescheduleCoachingSessionInstanceVariables,
} from 'coaching/mutations/__generated__/RescheduleCoachingSessionInstance';
import {
  SetUserStripeSource,
  SetUserStripeSourceVariables,
} from 'coaching/mutations/__generated__/SetUserStripeSource';
import {
  updateCoach as UpdateCoach,
  updateCoachVariables as UpdateCoachVariables,
} from 'coaching/mutations/__generated__/updateCoach';
import { errorFragment } from 'queries/__generated__/errorFragment';
import { AcceptCoachingInvitation } from 'coaching/mutations/__generated__/AcceptCoachingInvitation';
import ToastActions from 'actions/ToastActions';
import { Student } from 'coaching/containers/GetProspectiveStudentOptionsContainer';
import {
  SaveAvailabilitySchedule,
  SaveAvailabilityScheduleVariables,
} from 'coaching/mutations/__generated__/SaveAvailabilitySchedule';
import {
  SaveCoachPause,
  SaveCoachPauseVariables,
} from 'coaching/mutations/__generated__/SaveCoachPause';
import {
  CancelCoachPause,
  CancelCoachPauseVariables,
} from 'coaching/mutations/__generated__/CancelCoachPause';
import {
  SaveSingleDayAvailabilityOverride,
  SaveSingleDayAvailabilityOverrideVariables,
} from 'coaching/mutations/__generated__/SaveSingleDayAvailabilityOverride';
import {
  MaybeCreateUserAndDevice,
  MaybeCreateUserAndDeviceVariables,
} from 'coaching/mutations/__generated__/MaybeCreateUserAndDevice';
import {
  AddStudentToTutoringByDevice,
  AddStudentToTutoringByDeviceVariables,
} from 'coaching/queries/__generated__/AddStudentToTutoringByDevice';
import { RefundCharge, RefundChargeVariables } from 'coaching/mutations/__generated__/RefundCharge';
import {
  AvailableOrganizationCoachingSessions_coaching_availableOrganizationCoachingSessions_availableSessions,
  AvailableOrganizationCoachingSessions_coaching_availableOrganizationCoachingSessions_timeOff,
} from 'coaching/queries/__generated__/AvailableOrganizationCoachingSessions';

type PurchaseResult = {
  participantUuid: string | null;
  error?: { message: string; code?: string };
};

export function handleGQLError(error: errorFragment, consoleErrorText: string) {
  console.warn(error);
  console.error(`Coaching - ${consoleErrorText}`);
  ToastActions.showGQLError(error);
}

export function getCoachAvailabilityForEvent(
  coachUuid: string,
  eventDefinition: CoachingEventDefinitionInput,
  participantUuid: string
) {
  return RemindAPI.execute<GetCoachAvailabilityForEvent, GetCoachAvailabilityForEventVariables>({
    query: GET_COACH_AVAILABILITY_FOR_EVENT_QUERY,
    variables: {
      coachUuid,
      eventDefinition,
      participantUuid,
    },
    forceFetch: true,
  }).catch(err => {
    console.warn(err);
    console.error(
      `CoachingActions - could not get coach availability for event coach_uuid=${coachUuid} start_time=${eventDefinition.startDateTime} end_time=${eventDefinition.endDateTime} recurrence=${eventDefinition.recurrence}`
    );
  });
}

export function setUserStripeSource(stripeSourceId: string) {
  const handleError = (e: string, toastMessage: string | null) => {
    console.error(
      `CoachingActions - Could not add user card for stripeSourceId=${stripeSourceId}`,
      e
    );
    ToastActions.showErrorToast(
      toastMessage ?? 'There was a problem saving your card. Please try again.'
    );
  };

  return RemindAPI.execute<SetUserStripeSource, SetUserStripeSourceVariables>({
    mutation: SET_COACHING_USER_STRIPE_SOURCE,
    variables: {
      stripeSourceId,
      stripeAccountName: 'coaching',
    },
    refetchQueries: [
      {
        query: GET_COACHING_USER_STRIPE_SOURCE,
        variables: {
          stripeAccountName: 'coaching',
        },
      },
    ],
  })
    .then(res => {
      const { error, userFriendlyError } = res.setUserStripeSource;
      if (error) {
        handleError(error.message, userFriendlyError);
        return;
      }
      ToastActions.showSuccessToast('Payment method updated');
    })
    .catch(err => {
      handleError(err, null);
    });
}

export function createCoachingSession(
  input: CoachingSessionInput,
  refetchQueries?: {
    query: DocumentNode;
    variables: any;
  }[],
  onSuccess?: () => void
) {
  const handleError = (e: Error | string) => {
    console.error(`CoachingActions - could not create coaching session. err=${e.toString()}`);
    ToastActions.showErrorToast(
      'Could not create coaching session. Check r101-admin to make sure the user has not been deleted. If not, the coach may already have a session at that time - please check their existing sessions to verify.'
    );
    return;
  };
  return RemindAPI.execute<CreateCoachingSession, CreateCoachingSessionVariables>({
    mutation: CREATE_COACHING_SESSION_MUTATION,
    variables: { input },
    refetchQueries,
  })
    .then(res => {
      const { createCoachingSession } = res;
      if (!createCoachingSession || createCoachingSession.error) {
        return handleError(createCoachingSession?.error?.message!);
      }
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(handleError);
}

export function rescheduleCoachingSessionInstance(
  coachingSessionInstanceUuid: string,
  newStartDateTime: string,
  newEndDateTime: string,
  refetchQueries?: {
    query: DocumentNode;
    variables: any;
  }[],
  onSuccess?: () => void
) {
  const handleError = (e: Error | string) => {
    console.error(
      `CoachingActions - could not reschedule coaching session instance. err=${e.toString()}`,
      { coachingSessionInstanceUuid, newStartDateTime, newEndDateTime }
    );
    ToastActions.showErrorToast(e.toString());
    return;
  };
  return RemindAPI.execute<
    RescheduleCoachingSessionInstance,
    RescheduleCoachingSessionInstanceVariables
  >({
    mutation: RESCHEDULE_COACHING_SESSION_INSTANCE_MUTATION,
    variables: { coachingSessionInstanceUuid, newStartDateTime, newEndDateTime },
    refetchQueries,
  })
    .then(res => {
      const { participantRescheduleCoachingSessionInstance } = res;
      if (
        !participantRescheduleCoachingSessionInstance ||
        participantRescheduleCoachingSessionInstance.error
      ) {
        return handleError(participantRescheduleCoachingSessionInstance?.error?.message!);
      }
      if (onSuccess) {
        onSuccess();
      }
      ToastActions.showSuccessToast('Session successfully rescheduled');
    })
    .catch(handleError);
}

export function deleteCoachingSession(
  coachingSessionUuid: string,
  cancellationReason: string,
  coachingSessionInstanceUuid?: string,
  instanceStartDateTime?: string,
  refetchQueries?: {
    query: DocumentNode;
    variables: any;
  }[],
  onSuccess?: () => void
) {
  const errorText = `CoachingActions - could not delete session_uuid=${coachingSessionUuid} coaching_session_instance_uuid=${coachingSessionInstanceUuid}`;

  return RemindAPI.execute<DeleteCoachingSession, DeleteCoachingSessionVariables>({
    mutation: DELETE_COACHING_SESSION_MUTATION,
    variables: {
      coachingSessionUuid,
      instanceEventId: coachingSessionInstanceUuid,
      instanceStartDateTime,
      cancellationReason,
      coachingSessionInstanceUuid,
    },
    refetchQueries,
  })
    .then(res => {
      const { error } = res.deleteCoachingSession;
      if (error) {
        // We don't want to warn to rollbar as this is a known error path
        console.warn(error);
        ToastActions.showGQLError(error);
        return;
      }
      onSuccess?.();
      ToastActions.showSuccessToast('Session successfully deleted');
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast('Could not delete session');
    });
}

export function deleteStudentInvitation(participantUuid: string) {
  const errorText = `CoachingActions - could not delete student invitation for participant=${participantUuid}`;

  return RemindAPI.execute<DeleteStudentInvitation, DeleteStudentInvitationVariables>({
    mutation: DELETE_STUDENT_INVITATION_MUTATION,
    variables: {
      participantUuid,
    },
  })
    .then(res => {
      const { error } = res.coaching_deleteStudentInvitation;
      if (error) {
        // We don't want to warn to rollbar as this is a known error path
        console.warn(error);
        ToastActions.showGQLError(error);
        return;
      }
      ToastActions.showSuccessToast('Student invitation successfully deleted');
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast('Could not delete student invitation');
    });
}

// Both Instance and Schedule deletions use these paramaters
type DeleteCoachingSessionSharedParams = {
  scheduleUuid: string;
  cancellationReason: string;
  waiveCustomerLateCharge: boolean;
};

export function adminDeleteCoachingSessionInstance(
  sharedParams: DeleteCoachingSessionSharedParams,
  instanceUuid: string,
  refetchQueries?: {
    query: DocumentNode;
    variables: any;
  }[]
) {
  const errorText = `CoachingActions - coordinator could not delete instance instance_uuid=${instanceUuid} from schedule_uuid=${sharedParams.scheduleUuid}`;
  return RemindAPI.execute<
    AdminDeleteCoachingSessionInstance,
    AdminDeleteCoachingSessionInstanceVariables
  >({
    mutation: ADMIN_DELETE_COACHING_SESSION_INSTANCE,
    variables: {
      instanceUuid,
      ...sharedParams,
    },
    refetchQueries,
  })
    .then(res => {
      const { error } = res.coaching_adminDeleteCoachingSessionInstance;
      if (error) {
        console.warn(error);
        ToastActions.showGQLError(error);
        return;
      }
      ToastActions.showSuccessToast(`Session successfully deleted`);
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast(`Could not delete session`);
    });
}

export function adminDeleteCoachingSessionSchedule(
  sharedParams: DeleteCoachingSessionSharedParams,
  deleteFromInstanceUuid?: string,
  refetchQueries?: {
    query: DocumentNode;
    variables: any;
  }[]
) {
  const errorText = `CoachingActions - coordinator could not delete schedule schedule_uuid=${
    sharedParams.scheduleUuid
  } ${deleteFromInstanceUuid ? 'from instance instanceUuid=' + deleteFromInstanceUuid : ''}`;
  return RemindAPI.execute<
    AdminDeleteCoachingSessionSchedule,
    AdminDeleteCoachingSessionScheduleVariables
  >({
    mutation: ADMIN_DELETE_COACHING_SESSION_SCHEDULE,
    variables: {
      deleteFromInstanceUuid: deleteFromInstanceUuid ?? null,
      ...sharedParams,
    },
    refetchQueries,
  })
    .then(res => {
      const { error } = res.coaching_adminDeleteCoachingSessionSchedule;
      if (error) {
        console.warn(error);
        ToastActions.showGQLError(error);
        return;
      }
      ToastActions.showSuccessToast(`Sessions successfully deleted`);
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast(`Could not delete sessions`);
    });
}

export function createNewCoach(
  userUuid: string,
  subjects: string[],
  hourlyRateInCents: number,
  nameQuery: string
) {
  const errorText = `CoachingActions - could not create new coach userUuid=${userUuid} subjects=${subjects.join(
    ', '
  )}`;
  return RemindAPI.execute<CreateCoach, CreateCoachVariables>({
    mutation: CREATE_COACH_MUTATION,
    variables: {
      userUuid,
      subjects,
      hourlyRateInCents,
    },
    refetchQueries: [
      {
        query: COACHES_QUERY,
        variables: { nameQuery },
      },
    ],
  })
    .then(res => {
      const { error } = res.coaching_createCoach;
      if (error) {
        handleGQLError(error, errorText);
        return;
      }
      ToastActions.showSuccessToast('Coach successfully added');
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast('Could not add coach');
    });
}

export function getNameForUserUuid(userUuid: string) {
  return RemindAPI.execute<GetNameForUserUuid, GetNameForUserUuidVariables>({
    query: GET_NAME_FOR_USER_UUID_QUERY,
    variables: {
      userUuid,
    },
  })
    .then(res => res.getNameForUserUuid)
    .catch(() => {
      ToastActions.showErrorToast('Could not find user’s name');
      return undefined;
    });
}

export function reserveCoachingSession(
  startDateTime: string,
  endDateTime: string,
  coachUuid: string,
  purchaserUuid: string,
  subject: string
): Promise<{ reservationUuid: string; expiresAt: string } | null> {
  const handleError = (e: Error | string) => {
    console.error(`CoachingActions - could not reserve coaching session. err=${e}`);
    ToastActions.showErrorToast(
      'There was a problem reserving your coaching session. Please try again.'
    );
    return null;
  };
  return RemindAPI.execute<CreateCoachingReservation, CreateCoachingReservationVariables>({
    mutation: CREATE_COACHING_RESERVATION_MUTATION,
    variables: { subject, purchaserUuid, startDateTime, endDateTime, coachUuid },
  })
    .then(res => {
      const { createCoachingReservation: reservation } = res;
      if (!reservation?.reservationUuid || reservation.error) {
        return handleError(reservation?.error?.message!);
      }
      return {
        reservationUuid: reservation.reservationUuid,
        expiresAt: reservation.expiresAt!,
      };
    })
    .catch(handleError);
}

export function purchaseCoaching(
  startDateTime: string,
  endDateTime: string,
  coachUuid: string,
  stripeSource: string,
  purchaserUuid: string,
  timezone: string,
  subjects: string[],
  student: Partial<Student> & { name: string },
  studentGradeLevel: string,
  reservationUuid: string
): Promise<PurchaseResult> {
  const logError = (err: any, level: 'warn' | 'error'): void => {
    const text = 'Could not purchase coaching';
    const args = {
      purchaserUuid,
      studentUuid: student.uuid,
      coachUuid,
      startDateTime,
      reservationUuid,
      err,
    };

    if (level === 'error') {
      errorLogger(text)(args);
    } else if (level === 'warn') {
      console.warn(text, args);
    }
  };

  const handlePurchaseResponse = (
    res:
      | PurchaseCoachingProductWithConnectedStudent_coaching_purchaseCoachingSessionWithConnectedStudent
      | PurchaseCoachingProductWithNoConnectedStudent_coaching_purchaseCoachingSessionWithNoConnectedStudent
  ): Promise<PurchaseResult> => {
    const { participantUuid, error: resError } = res;

    if (resError) {
      // no need to log as an error since these are expected errors
      logError(resError, 'warn');
    }

    return Promise.resolve({
      participantUuid,
      error: resError ? { message: resError.message, code: resError.code ?? undefined } : undefined,
    });
  };

  const sharedVars = {
    startDateTime,
    endDateTime,
    coachUuid,
    stripeSource,
    purchaserUuid,
    timezone,
    subjects,
    studentGradeLevel,
    reservationUuid,
  };

  const promise = student.uuid
    ? RemindAPI.execute<
        PurchaseCoachingProductWithConnectedStudent,
        PurchaseCoachingProductWithConnectedStudentVariables
      >({
        mutation: PURCHASE_COACHING_WITH_CONNECTED_STUDENT_MUTATION,
        variables: { ...sharedVars, studentUuid: student.uuid },
      }).then(res =>
        handlePurchaseResponse(res.coaching_purchaseCoachingSessionWithConnectedStudent)
      )
    : RemindAPI.execute<
        PurchaseCoachingProductWithNoConnectedStudent,
        PurchaseCoachingProductWithNoConnectedStudentVariables
      >({
        mutation: PURCHASE_COACHING_WITH_NO_CONNECTED_STUDENT_MUTATION,
        variables: { ...sharedVars, studentName: student.name },
      }).then(res =>
        handlePurchaseResponse(res.coaching_purchaseCoachingSessionWithNoConnectedStudent)
      );

  return promise.catch(err => {
    // if we get here, it means we encountered an unexpected error
    // so we should log as an error
    logError(err, 'error');
    return {
      participantUuid: null,
      error: { message: 'We were unable to complete your coaching purchase' },
    };
  });
}

export function patchParticipant(uuid: string, participant: CoachingParticipantInput) {
  const errorText = `CoachingActions - could not create modify participant uuid=${uuid}`;
  return RemindAPI.execute<PatchParticipant, PatchParticipantVariables>({
    mutation: PATCH_PARTICIPANT_MUTATION,
    variables: {
      uuid,
      participant,
    },
  })
    .then(res => {
      const { error } = res.patchParticipant;
      if (error) {
        handleGQLError(error, errorText);
        return { success: false };
      }
      updateParticipant(uuid, participant);
      ToastActions.showSuccessToast('Participant successfully modified');
      return { success: true };
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast('Could not modify participant');
      return { success: false };
    });
}

type AddStudentArgs = {
  userDevice: string;
  participantUUID: string;
  // Only used by the new link student endpoint logic(addStudentToTutoringByDevice)
  linkStudentToken?: string;
  // Only used by the old link student endpoint logic(addStudentByDevice)
  userEmail?: string;
};

export type AddStudentFn = (args: AddStudentArgs) => Promise<{ success: boolean }>;

export function addStudentByDevice(args: AddStudentArgs): Promise<{ success: boolean }> {
  const errorText = `Could not add student`;
  return RemindAPI.execute<AddStudentToCoachingByDevice, AddStudentToCoachingByDeviceVariables>({
    mutation: ADD_STUDENT_MUTATION,
    variables: {
      deviceAddress: args.userDevice,
      participantUUID: args.participantUUID,
    },
  })
    .then(res => {
      const { error } = res.addStudentToCoachingByDevice;
      if (error) {
        console.warn(error);
        ToastActions.showErrorToast(
          `We were unable to add your student’s account at this time. Please email us at ${CoachingConstants.COACHING_HELP_EMAIL} for further assistance.`
        );
        return { success: false };
      }
      ToastActions.showSuccessToast(
        `We’ve sent an invitation to ${args.userDevice}${args.userEmail &&
          ` and a confirmation to ${args.userEmail}`}`
      );
      return { success: true };
    })
    .catch(err => {
      errorLogger(errorText)({ err, participantUUid: args.participantUUID });
      ToastActions.showErrorToast(
        `We were unable to add your student’s account at this time. Please email us at ${CoachingConstants.COACHING_HELP_EMAIL} for further assistance.`
      );
      return { success: false };
    });
}

export function addStudentToTutoringByDevice(args: AddStudentArgs) {
  const errorText = `Could not add student`;
  return RemindAPI.execute<AddStudentToTutoringByDevice, AddStudentToTutoringByDeviceVariables>({
    mutation: ADD_STUDENT_TO_TUTORING_MUTATION,
    variables: {
      studentEmailOrSMSDeviceAddress: args.userDevice,
      participantUUID: args.participantUUID,
      addStudentToken: args.linkStudentToken ?? null,
    },
  })
    .then(res => {
      const { error } = res.coaching_addStudentToTutoringByDevice;
      if (error) {
        console.warn(error);
        ToastActions.showErrorToast(
          `We were unable to add your student’s account at this time. Please email us at ${CoachingConstants.COACHING_HELP_EMAIL} for further assistance.`
        );
        return { success: false };
      }
      ToastActions.showSuccessToast(
        `We’ve sent an invitation to ${args.userDevice}${args.userEmail &&
          ` and a confirmation to ${args.userEmail}`}`
      );
      return { success: true };
    })
    .catch(err => {
      errorLogger(errorText)({ err, participantUUID: args.participantUUID });
      ToastActions.showErrorToast(
        `We were unable to add your student’s account at this time. Please email us at ${CoachingConstants.COACHING_HELP_EMAIL} for further assistance.`
      );
      return { success: false };
    });
}

export function acceptCoachingInvitation() {
  const errorText = 'User could not accept coaching invitation';

  return RemindAPI.execute<AcceptCoachingInvitation>({ mutation: ACCEPT_COACHING_INVITATION })
    .then(res => {
      const { error } = res.acceptCoachingInvitation;

      if (error) {
        // We don't want to warn to rollbar as this is a known error path
        console.warn(error);
        ToastActions.showGQLError(error);
        return { success: false };
      }
      ToastActions.showSuccessToast('Coaching invitation was accepted');
      return { success: true };
    })
    .catch(err => {
      errorLogger(errorText)({ err });
      ToastActions.showErrorToast('We were unable to confirm your invitation to coaching');
      return { success: false };
    });
}

function updateParticipant(uuid: string, participantInput: CoachingParticipantInput) {
  // updates a specific participant in the Apollo cache
  return getApolloClientSingleton().writeFragment({
    id: dataIdFromObject({ __typename: 'CoachingParticipant', uuid }),
    fragment: gql`
      fragment participant on CoachingParticipant {
        studentName
        studentUuid
        whiteboardUrl
      }
    `,
    variables: { uuid },
    data: {
      studentUuid: participantInput.studentUuid,
      studentName: participantInput.studentName,
      whiteboardUrl: participantInput.whiteboardUrl,
      __typename: 'CoachingParticipant',
    },
  });
}

export function getCoachCreateFields(): Promise<{
  subjects?: string[];
  hourlyRateInCents?: number[];
  error?: string;
}> {
  return RemindAPI.execute<GetCreateCoachFields>({
    query: GET_CREATE_COACH_FIELDS,
    variables: {},
    forceFetch: true,
  })
    .then(res => {
      const subjectsResult = res.coaching_coachableSubjects;
      const hourRatesResult = res.coaching_coachingHourlyRates;
      return {
        subjects: subjectsResult?.subjects || [],
        hourlyRateInCents: hourRatesResult?.hourlyRatesInCents || [],
      };
    })
    .catch(err => {
      console.warn(err);
      console.error('CoachingActions - could not get coach fields create container');
      return { error: err.message };
    });
}

export function getCoachStatusValues() {
  return RemindAPI.execute<GetCoachStatusValues>({
    query: GET_COACH_STATUS_VALUES,
    variables: {},
    forceFetch: true,
  })
    .then(res => {
      const coachStatusesResult = res.coaching_coachStatuses;
      return { statuses: coachStatusesResult?.statuses };
    })
    .catch(err => {
      console.warn(err);
      console.error('CoachingActions - could not get coach fields create container');
      return { error: err };
    });
}

export function updateCoach(
  userUuid: string,
  subjects: string[],
  status: string,
  nameQuery: string
) {
  const errorText = `CoachingActions - could not create update coach userUuid=${userUuid} subjects=${subjects.join(
    ', '
  )}`;
  return RemindAPI.execute<UpdateCoach, UpdateCoachVariables>({
    mutation: UPDATE_COACH_MUTATION,
    variables: {
      userUuid,
      subjects,
      status,
    },
    refetchQueries: [
      {
        query: COACHES_QUERY,
        variables: { nameQuery },
      },
    ],
  })
    .then(res => {
      const { error } = res.coaching_updateCoach;
      if (error) {
        handleGQLError(error, errorText);
        return;
      }
      ToastActions.showSuccessToast('Coach successfully added');
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast('Could not add coach');
    });
}

export function saveAvailabilitySchedule(
  schedule: AvailabilityScheduleInput,
  timezone: string,
  queryToRefetch?: { query: DocumentNode; variables?: Object }
) {
  const errorText = 'Could not save availability schedule for coach';
  return RemindAPI.execute<SaveAvailabilitySchedule, SaveAvailabilityScheduleVariables>({
    mutation: SAVE_AVAILABILITY_SCHEDULE_MUTATION,
    variables: {
      schedule,
      timezone,
    },
    refetchQueries: queryToRefetch ? [queryToRefetch] : [],
  })
    .then(res => {
      const { error } = res.coaching_saveCoachAvailabilitySchedule;
      if (error) {
        ToastActions.showErrorToast(
          `There was a problem updating your schedule. If you continue to encounter problems, please contact support at ${CoachingConstants.COACHING_HELP_EMAIL}.`
        );
        return;
      }
      ToastActions.showSuccessToast('Schedule saved successfully');
    })
    .catch(err => {
      errorLogger(errorText)({ schedule, timezone, err });
      ToastActions.showErrorToast('Could not save schedule');
    });
}

export function saveCoachPause(
  input: SaveCoachPauseInput,
  queryToRefetch?: { query: DocumentNode; variables?: Object }
) {
  const errorText = 'Could not save coach pause';
  return RemindAPI.execute<SaveCoachPause, SaveCoachPauseVariables>({
    mutation: SAVE_COACH_PAUSE_MUTATION,
    variables: {
      input,
    },
    refetchQueries: queryToRefetch ? [queryToRefetch] : [],
  })
    .then(res => {
      const { error } = res.coaching_saveCoachPause;
      if (error) {
        handleGQLError(error, errorText);
        return;
      }
      ToastActions.showSuccessToast('Coach pause saved successfully');
    })
    .catch(err => {
      errorLogger(errorText)({ input, err });
      ToastActions.showErrorToast(errorText);
    });
}

export function cancelCoachPause(
  input: CancelCoachPauseVariables,
  queryToRefetch?: { query: DocumentNode; variables?: Object }
) {
  const errorText = 'Could not cancel coach pause';
  return RemindAPI.execute<CancelCoachPause>({
    mutation: CANCEL_COACH_PAUSE_MUTATION,
    variables: input,
    refetchQueries: queryToRefetch ? [queryToRefetch] : [],
  })
    .then(res => {
      const { error } = res.coaching_cancelCoachPause;
      if (error) {
        handleGQLError(error, errorText);
        return;
      }
      ToastActions.showSuccessToast('Coach pause saved successfully');
    })
    .catch(err => {
      errorLogger(errorText)({ input, err });
      ToastActions.showErrorToast(errorText);
    });
}

export function saveSingleDayAvailabilityOverride(
  override: SingleDayAvailabilityOverrideInput,
  queryToRefetch?: { query: DocumentNode; variables?: Object }
) {
  const errorText = 'Could not save single day availability override for coach';
  return RemindAPI.execute<
    SaveSingleDayAvailabilityOverride,
    SaveSingleDayAvailabilityOverrideVariables
  >({
    mutation: SAVE_SINGLE_DAY_AVAILABILITY_OVERRIDE_MUTATION,
    variables: {
      override,
    },
    refetchQueries: queryToRefetch ? [queryToRefetch] : [],
  })
    .then(res => {
      const { error } = res.coaching_saveSingleDayAvailabilityOverride;
      if (error) {
        handleGQLError(error, errorText);
        return;
      }
      ToastActions.showSuccessToast('Availability saved successfully');
    })
    .catch(err => {
      errorLogger(errorText)({ override, err });
      ToastActions.showErrorToast('Could not save availability');
    });
}

export function getStudentsFamilyMembers(): Promise<{
  students?: GetStudentsFamilyMembers_coaching_studentFamilyMembers[];
  error?: string;
}> {
  return RemindAPI.execute<GetStudentsFamilyMembers>({
    query: GET_STUDENTS_FAMILY_MEMBERS_DATA_QUERY,
    variables: {},
    forceFetch: true,
  })
    .then(res => {
      return { students: res.coaching_studentFamilyMembers };
    })
    .catch(err => {
      console.warn(err);
      console.error('CoachingActions - could not get family members', err);

      reloadIfUnauthenticated(err);

      return { error: err.message };
    });
}

export type ProspectiveOptionsResult = {
  subjects?: string[];
  gradeLevels?: string[];
  error?: string;
};

export function getProspectiveOptions(): Promise<ProspectiveOptionsResult> {
  return RemindAPI.execute<GetProspectiveOptions>({
    query: GET_PROSPECTIVE_OPTIONS_DATA_QUERY,
    variables: {},
    forceFetch: true,
  })
    .then(res => {
      return {
        subjects: res.coaching_coachableSubjects.subjects.sort(),
        gradeLevels: res.coaching_gradeLevels.gradeLevels.sort((str1, str2) => {
          // sort grade levels from lowest to highest (2nd to 12th)
          return parseInt(str1, 10) - parseInt(str2, 10);
        }),
      };
    })
    .catch(err => {
      console.warn(err);
      console.error('CoachingActions - could not get subjects and grade levels', err);
      return { error: err.message };
    });
}

type MaybeCreateUserAndDeviceResponse = {
  deviceConfirmationRequired?: boolean;
  error?: string;
};
export function maybeCreateUserAndDevice(input: {
  name: string;
  email: string;
  role: PurchaserRole;
}): Promise<MaybeCreateUserAndDeviceResponse> {
  return RemindAPI.execute<MaybeCreateUserAndDevice, MaybeCreateUserAndDeviceVariables>({
    mutation: MAYBE_CREATE_USER_AND_DEVICE,
    variables: { ...input },
  })
    .then(res => {
      return {
        deviceConfirmationRequired:
          res.coaching_maybeCreateUserAndDevice.deviceConfirmationRequired ?? undefined,
      };
    })
    .catch(err => {
      console.warn(err);
      console.error('CoachingActions - could not check device confirmation');
      return { error: err.message };
    });
}

export type CreateTutoringReservationResponse = {
  reservationUuid?: string | null;
  error?: string | null;
};

export function createTutoringReservation(input: {
  coachUuid: string;
  startDateTime: string;
  endDateTime: string;
  subject: string;
}): Promise<CreateTutoringReservationResponse> {
  return RemindAPI.execute<CreateTutoringReservation, CreateTutoringReservationVariables>({
    mutation: CREATE_TUTORING_RESERVATION_MUTATION,
    variables: { ...input },
  })
    .then(res => {
      return {
        reservationUuid: res.coaching_createTutoringReservation?.reservationUuid,
        error: res.coaching_createTutoringReservation?.error?.message,
      };
    })
    .catch(err => {
      console.warn(err);
      console.error('CoachingActions - could not create tutoring reservation', err);
      return { error: err.message };
    });
}

export type ConfirmTutoringReservationResponse = {
  participantUuid?: string | null;
  addStudentToken?: string;
  error?: ConfirmTutoringReservation_coaching_confirmTutoringReservation_error | null;
};

export function confirmTutoringReservation(
  input: ConfirmTutoringReservationVariables
): Promise<ConfirmTutoringReservationResponse> {
  return RemindAPI.execute<ConfirmTutoringReservation, ConfirmTutoringReservationVariables>({
    mutation: CONFIRM_TUTORING_RESERVATION_MUTATION,
    variables: { ...input },
  })
    .then(res => {
      return {
        participantUuid: res.coaching_confirmTutoringReservation?.participantUuid,
        addStudentToken: res.coaching_confirmTutoringReservation?.addStudentToken ?? undefined,
        error: res.coaching_confirmTutoringReservation?.error,
      };
    })
    .catch((err: Error) => {
      console.warn(err);
      console.error('CoachingActions - could not confirm tutoring reservation', err);
      return {
        error: {
          __typename: 'Error',
          message: err.message,
          code: null,
          fieldErrors: [],
        },
      };
    });
}

export type CoachingAvailabilityResponse = {
  availableSessions?: (AvailableCoachingSessions_coaching_availableCoachingSessions_availableSessions | null)[];
  timeOff?: (AvailableCoachingSessions_coaching_availableCoachingSessions_timeOff | null)[];
  error?: string;
};

export function getAvailabilityCoachingSessions(
  input: AvailableCoachingSessionsVariables
): Promise<CoachingAvailabilityResponse> {
  return RemindAPI.execute<AvailableCoachingSessions>({
    query: AVAILABLE_COACHING_SESSIONS_QUERY,
    variables: {
      subjects: input.subjects,
      startDateTimeRange: input.startDateTimeRange,
      endDateTimeRange: input.endDateTimeRange,
    },
    forceFetch: true,
  })
    .then(res => {
      return {
        availableSessions: res.coaching_availableCoachingSessions?.availableSessions,
        timeOff: res.coaching_availableCoachingSessions?.timeOff,
      };
    })
    .catch((err: Error) => {
      console.warn(err);
      console.error('CoachingActions - could not get coaching availability response', err);
      return { error: err.message };
    });
}

export function refundCharge(
  input: {
    instanceUuid: string;
    participantUuid: string;
    chargeUuid: string;
    refundReason: string;
  },
  onInsufficientPermissions?: () => void,
  refetchQueries?: {
    query: DocumentNode;
    variables: any;
  }[]
) {
  const { instanceUuid, participantUuid, chargeUuid, refundReason } = input;
  const errorText = `CoachingActions - coordinator could not issue refund for instanceUuid=${instanceUuid}, participantUuid=${participantUuid}`;
  return RemindAPI.execute<RefundCharge, RefundChargeVariables>({
    mutation: REFUND_CHARGE,
    variables: {
      instanceUuid,
      participantUuid,
      chargeUuid,
      refundReason,
    },
    refetchQueries,
  })
    .then(res => {
      const { error } = res.coaching_refundCharge;
      if (error && error.code === 'USER_CANNOT_ISSUE_REFUNDS') {
        onInsufficientPermissions?.();
        return;
      } else if (error) {
        console.warn(error);
        ToastActions.showGQLError(error);
        return;
      }
      ToastActions.showSuccessToast(`Refund successful`);
    })
    .catch(err => {
      console.warn(err);
      console.error(errorText);
      ToastActions.showErrorToast(`Refund failed`);
    });
}
